/**
 * localStorage is plain text the player (or a stale build from a past
 * version) can edit by hand, and a saved blob outlives the code that wrote
 * it. A `{ ...defaults, ...parsed }` spread lets any field of the wrong
 * runtime type ride straight through into state the rest of the app assumes
 * is well-typed — the crash then happens far away, at the point of use
 * (a `.push` on a `null`, a `.includes` on a `null`), not here at load.
 *
 * `sanitize` copies each field of `raw` across only when it passes its
 * validator (or, for fields with no validator, when its runtime type matches
 * the default's), and otherwise silently keeps the default — a corrupt or
 * hand-edited blob degrades quietly instead of throwing. Unknown extra keys
 * are preserved, matching the old spread's behaviour.
 */
export type Validators<T> = { [K in keyof T]?: (value: unknown) => boolean };

export function sanitize<T extends object>(
  defaults: T,
  raw: unknown,
  validators: Validators<T> = {}
): T {
  const defaultsRecord = defaults as Record<string, unknown>;
  const validatorsRecord = validators as unknown as Record<string, ((v: unknown) => boolean) | undefined>;
  // Deep-copied, not spread. Callers keep their defaults in a module-level
  // constant, so handing back one of its arrays or objects by reference would
  // let the caller's own writes (`stats.days.push(...)`) mutate that shared
  // default in place and poison every later load that falls back to it.
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(defaultsRecord)) result[key] = cloneDefault(defaultsRecord[key]);
  if (raw !== null && typeof raw === 'object') {
    const parsed = raw as Record<string, unknown>;
    for (const key of Object.keys(parsed)) {
      const value = parsed[key];
      if (key in defaultsRecord) {
        const validator = validatorsRecord[key];
        const ok = validator ? validator(value) : sameRuntimeType(defaultsRecord[key], value);
        if (ok) result[key] = value;
      } else {
        // Unknown key — not part of the shape we know, but the previous
        // spread-based loaders kept it too, so do the same here.
        result[key] = value;
      }
    }
  }
  return result as T;
}

/** A default value the caller can safely mutate, sharing nothing with the
 *  caller's own defaults constant. */
function cloneDefault(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(cloneDefault);
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    const source = value as Record<string, unknown>;
    for (const key of Object.keys(source)) out[key] = cloneDefault(source[key]);
    return out;
  }
  return value;
}

/** Fallback check used when a field has no explicit validator: the saved
 *  value must share the default's runtime shape. A `null` default means the
 *  field is nullable in a way this generic check cannot infer (it could be
 *  "number or null", "string or null", ...) — callers must supply an
 *  explicit validator (see `orNull`) for those fields; until they do, this
 *  conservatively rejects everything and keeps the default. */
function sameRuntimeType(defaultValue: unknown, value: unknown): boolean {
  if (Array.isArray(defaultValue)) return Array.isArray(value);
  if (typeof defaultValue === 'number') return isFiniteNumber(value);
  if (defaultValue !== null && typeof defaultValue === 'object') return isPlainObject(value);
  if (defaultValue === null) return false;
  return typeof value === typeof defaultValue;
}

export function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export function isString(v: unknown): v is string {
  return typeof v === 'string';
}

export function isBoolean(v: unknown): v is boolean {
  return typeof v === 'boolean';
}

export function isArray(v: unknown): v is unknown[] {
  return Array.isArray(v);
}

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Wraps a type check to also accept `null`, for fields whose default is
 *  `null` but which hold a typed value once set. */
export function orNull(check: (v: unknown) => boolean): (v: unknown) => boolean {
  return (v: unknown) => v === null || check(v);
}
